// ─────────────────────────────────────────────────────────────────────────────
// src/lib/gtm.js
// Complete GTM + GA4 utility for ShippingChimp React App
// UTMs stored in cookies (cross-subdomain) not sessionStorage
// ─────────────────────────────────────────────────────────────────────────────
import { LABELGENAPI } from './API';

export const GTM_ID      = 'GTM-TVKVNP5'
export const GA4_ID      = 'G-MY0VDTVCV3'
export const ROOT_DOMAIN = '.shippingchimp.com'   // leading dot = all subdomains
export const UTM_DAYS    = 30

// ── Cookie helpers ────────────────────────────────────────────────────────────

function setCookie(name, value, days) {
    const expires = new Date()
    expires.setDate(expires.getDate() + days)
    document.cookie = [
        `${name}=${encodeURIComponent(value)}`,
        `expires=${expires.toUTCString()}`,
        `path=/`,
        `domain=${ROOT_DOMAIN}`,
        `SameSite=Lax`
    ].join('; ')
}

function getCookie(name) {
    const match = document.cookie.match(
        new RegExp('(?:^|;\\s*)' + name + '=([^;]*)')
    )
    return match ? decodeURIComponent(match[1]) : null
}

// ── Safe gtag — never throws "gtag is not defined" ───────────────────────────

export function gtag(...args) {
    if (typeof window === 'undefined') return
    window.dataLayer = window.dataLayer || []
    if (typeof window.gtag === 'function') {
        window.gtag(...args)
    } else {
        window.dataLayer.push(args)
    }
}

// ── Push event to dataLayer ───────────────────────────────────────────────────

export function pushToDataLayer(data) {
    window.dataLayer = window.dataLayer || []
    window.dataLayer.push(data)
}

// ── Read GA4 client_id from _ga cookie ───────────────────────────────────────

export function getGA4ClientId() {
    const match = document.cookie.match(/_ga=GA\d+\.\d+\.(\d+\.\d+)/)
    return match ? match[1] : null
}

// ── Persist UTMs from URL → cookie (cross-subdomain) ─────────────────────────
// Call this on every page load via useEffect in App.jsx

export function persistUTMs() {
    const params = new URLSearchParams(window.location.search)
    const keys   = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content']
    let   saved  = false

    keys.forEach(key => {
        if (params.get(key)) {
            setCookie(key, params.get(key), UTM_DAYS)
            saved = true
        }
    })

    if (saved) {
        setCookie('utm_captured_at',  new Date().toISOString(), UTM_DAYS)
        setCookie('utm_captured_url', window.location.href,     UTM_DAYS)
        console.log('[SC UTM] Saved to cookie:', getStoredUTMs())
    }
}

// ── Read stored UTMs — from cookie (works cross-subdomain) ───────────────────

export function getStoredUTMs() {
    return {
        utm_source  : getCookie('utm_source')   || '',
        utm_medium  : getCookie('utm_medium')   || '',
        utm_campaign: getCookie('utm_campaign') || '',
        utm_term    : getCookie('utm_term')     || '',
        utm_content : getCookie('utm_content')  || '',
    }
}

// ── Set user_id in GA4 ────────────────────────────────────────────────────────

export function setGA4UserId(userId) {
    const id = String(userId)
    gtag('config', GA4_ID, { user_id: id })
    gtag('set', 'user_properties', { dl_user_id: id })
    pushToDataLayer({ user_id: id, dl_user_id: id })
}

// ── Track registration ────────────────────────────────────────────────────────

export async function trackRegistration(profile) {
    const utms     = getStoredUTMs()
    const clientId = getGA4ClientId()
    const userId   = String(profile.id)

    // 1. Set user_id in GA4
    setGA4UserId(userId)

    // 2. Push event to GTM dataLayer
    pushToDataLayer({
        event        : 'sc_user_registered',
        user_id      : userId,
        dl_user_id   : userId,
        ga4_client_id: clientId,
        ...utms
    })

    // 3. Save to ShippingChimp backend
    try {
        return LABELGENAPI.post('/update-analytics', {
            user_id  : profile.id,
            client_id: clientId,
            ...utms
        })
    } catch (err) {
        console.error('[GTM] Analytics save failed:', err)
    }
}

// ── Track login (links existing users to GA4) ─────────────────────────────────

export async function trackLogin(profile) {
    const utms     = getStoredUTMs()
    const clientId = getGA4ClientId()
    const userId   = String(profile.id)

    // 1. Set user_id in GA4
    setGA4UserId(userId)

    // 2. Push event to GTM dataLayer
    pushToDataLayer({
        event        : 'sc_user_logged_in',
        user_id      : userId,
        dl_user_id   : userId,
        ga4_client_id: clientId,
        ...utms
    })

    // 3. Save to backend
    try {
        return LABELGENAPI.post('/update-analytics', {
            user_id  : profile.id,
            client_id: clientId,
            ...utms
        })
    } catch (err) {
        console.error('[GTM] Analytics save failed:', err)
    }
}